import { useEffect, useMemo, useState } from 'react';
import ReactFinalForm from 'components/form/ReactFinalForm';
import { IMaintenance, IStatusOption } from 'interfaces';
import { IdentityField } from 'components/form/IdentityField';
import { useTranslation } from 'react-i18next';
import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import { maintenanceService } from 'services/api';
import dayjs from 'dayjs';
import { DatePickerAdapter } from 'components/form/DatePickerAdapter';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';

const CLOSURE_STATUS_VALUES = [2, 3, 4];

interface CloseMaintenanceFormParams {
  onSubmit: any;
  maintenance?: IMaintenance | null;
}

function CloseMaintenanceForm(params: CloseMaintenanceFormParams) {
  const { t } = useTranslation();
  const { onSubmit, maintenance } = params;

  const [statusOptions, setStatusOptions] = useState<IStatusOption[]>([]);

  const translateStatus = (displayName: string) =>
    t(`pages.maintenance.status_options.${displayName}`, {
      defaultValue: displayName
    });

  useEffect(() => {
    maintenanceService.options().then((res) => {
      if (res.actions && res.actions.POST) {
        const choices = res.actions.POST.status.choices as IStatusOption[];
        const closureOptions = choices
          .filter((item) => CLOSURE_STATUS_VALUES.includes(item.value))
          .map((item) => ({
            ...item,
            display_name: translateStatus(item.display_name)
          }));

        const currentValue = maintenance?.status?.value;
        const currentInClosure =
          currentValue != null && CLOSURE_STATUS_VALUES.includes(currentValue);

        if (currentValue != null && !currentInClosure && maintenance?.status) {
          const currentOption = choices.find((item) => item.value === currentValue);
          if (currentOption) {
            closureOptions.unshift({
              ...currentOption,
              display_name: translateStatus(currentOption.display_name)
            });
          } else {
            closureOptions.unshift({
              value: maintenance.status.value,
              display_name: translateStatus(maintenance.status.display_name)
            });
          }
        }

        setStatusOptions(closureOptions);
      }
    });
  }, [maintenance?.status?.value, maintenance?.status?.display_name, t]);

  const statusInitValue = useMemo(() => {
    if (!maintenance?.status) {
      return undefined;
    }
    return {
      ...maintenance.status,
      display_name: translateStatus(maintenance.status.display_name)
    };
  }, [maintenance?.status, t]);

  return (
    <div>
      <ReactFinalForm onSubmit={onSubmit} formId="closeMaintenance" hideControls>
        <div className="flex flex-wrap">
          <div className="w-full md:w-1/2 p-2">
            <IdentityField
              name="execution_date"
              component={DatePickerAdapter}
              label={t(`global.execution_date`)}
              initialValue={dayjs().format('YYYY-MM-DD')}
              type="text"
              required
            />
          </div>
          <div className="p-2 w-full md:w-1/2">
            <IdentityField
              name="status"
              label={t(`global.status`)}
              component={AutocompleteAdapter}
              options={statusOptions}
              initValue={statusInitValue}
              type="text"
              optionLabel="display_name"
              optionValue="value"
              required
            />
          </div>
          <div className="p-2 w-full">
            <IdentityField
              name="notes"
              label={t(`global.notes`)}
              component={TextFieldAdapter}
              initialValue={maintenance?.notes ?? ''}
              type="text"
              inputProps={{
                multiline: true,
                minRows: 2
              }}
            />
          </div>
        </div>
      </ReactFinalForm>
    </div>
  );
}

export default CloseMaintenanceForm;
